/**
 * Knex implementation of DatabaseAdapter.
 *
 * Provides normalized database access using Knex query builder.
 * Supports SQLite, MySQL, and PostgreSQL.
 */

import type { Knex } from 'knex'
import type {
  DatabaseAdapter,
  PaginatedResult,
  ServerEntityQuery
} from '@gzl10/nexus-sdk'
import { applyFilters } from './filter-helpers.js'
import { ConflictError, ValidationError, AppError, ErrorCodes } from '../../core/errors/app-error.js'
import { logger } from '@kernel/logger/index.js'

/**
 * Default pagination values
 */
const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 20
const DEFAULT_MAX_LIMIT = 100

/**
 * Detect and rethrow database constraint errors as typed AppErrors.
 * Supports SQLite, MySQL, and PostgreSQL error patterns.
 */
export function handleDbError(err: unknown): never {
  if (err instanceof AppError) throw err

  const dbErr = err as { code?: string; errno?: number; message?: string }
  const msg = dbErr.message ?? ''
  const code = dbErr.code ?? ''

  // UNIQUE constraint: PG 23505, MySQL ER_DUP_ENTRY/1062, SQLite text
  if (code === '23505' || code === 'ER_DUP_ENTRY' || dbErr.errno === 1062 || msg.includes('UNIQUE constraint failed')) {
    throw new ConflictError(ErrorCodes.DB_CONSTRAINT_UNIQUE, 'Unique constraint violation')
  }

  // Foreign key: PG 23503, MySQL ER_NO_REFERENCED_ROW_2/1452, SQLite text
  if (code === '23503' || code === 'ER_NO_REFERENCED_ROW_2' || dbErr.errno === 1452 || msg.includes('FOREIGN KEY constraint failed')) {
    throw new ValidationError(ErrorCodes.DB_CONSTRAINT_FK, [
      { path: 'foreignKey', message: 'Foreign key constraint violation' }
    ])
  }

  // Table not found: PG 42P01, MySQL ER_NO_SUCH_TABLE/1146, SQLite text
  if (code === '42P01' || code === 'ER_NO_SUCH_TABLE' || dbErr.errno === 1146 || msg.includes('no such table')) {
    logger.error({ err }, 'Database table not found — database may need migration or was wiped')
    throw new AppError({ code: ErrorCodes.DATABASE_NOT_READY, message: 'Database not ready' }, 503)
  }

  logger.error({ err }, 'Unexpected database error')
  throw new AppError({ code: ErrorCodes.SYSTEM_INTERNAL_ERROR, message: 'Internal database error' }, 500)
}

/**
 * Knex-based implementation of DatabaseAdapter.
 *
 * @example
 * const adapter = new KnexAdapter(knexInstance)
 * const users = await adapter.findMany<User>('users', {
 *   filters: { status: { $eq: 'active' } },
 *   sort: 'createdAt',
 *   order: 'desc'
 * })
 */
export class KnexAdapter implements DatabaseAdapter {
  constructor(private readonly knex: Knex) {}

  /**
   * Raw access to Knex instance.
   * Use sparingly - prefer typed methods.
   */
  get raw(): unknown {
    return this.knex
  }

  async findMany<T = unknown>(table: string, query?: ServerEntityQuery): Promise<PaginatedResult<T>> {
    try {
      const maxLimit = query?.maxLimit ?? DEFAULT_MAX_LIMIT
      const page = Math.max(1, query?.page ?? DEFAULT_PAGE)
      const limit = Math.min(maxLimit, Math.max(1, query?.limit ?? DEFAULT_LIMIT))
      const offset = (page - 1) * limit

      // Build base query
      let qb = this.knex(table)

      // Apply filters
      if (query?.filters) {
        qb = applyFilters(qb, query.filters)
      }

      // Get total count
      const countResult = await qb.clone().count('* as count').first<{ count: string | number }>()
      const total = typeof countResult?.count === 'string'
        ? parseInt(countResult.count, 10)
        : (countResult?.count ?? 0)

      // Apply sorting
      if (query?.sort) {
        qb = qb.orderBy(query.sort, query.order ?? 'asc')
      }

      // Apply pagination
      const items = await qb.limit(limit).offset(offset) as T[]

      const totalPages = Math.ceil(total / limit)

      return {
        items,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages
      }
    } catch (err) {
      handleDbError(err)
    }
  }

  async findOne<T = unknown>(table: string, filters: Record<string, unknown>): Promise<T | null> {
    try {
      let qb = this.knex(table)
      qb = applyFilters(qb, filters)
      const result = await qb.first()
      return (result as T) ?? null
    } catch (err) {
      handleDbError(err)
    }
  }

  async findById<T = unknown>(table: string, id: string): Promise<T | null> {
    try {
      const result = await this.knex(table).where('id', id).first()
      return (result as T) ?? null
    } catch (err) {
      handleDbError(err)
    }
  }

  async count(table: string, filters?: Record<string, unknown>): Promise<number> {
    let qb = this.knex(table)

    if (filters) {
      qb = applyFilters(qb, filters)
    }

    const result = await qb.count('* as count').first<{ count: string | number }>()
    return typeof result?.count === 'string'
      ? parseInt(result.count, 10)
      : (result?.count ?? 0)
  }

  async insert<T = unknown>(table: string, data: Record<string, unknown>): Promise<T> {
    try {
      const [id] = await this.knex(table).insert(data).returning('id')
      // For SQLite, returning() returns the id directly; for others, it's an object
      const insertedId = typeof id === 'object' ? (id as { id: string }).id : id
      const result = await this.findById<T>(table, insertedId)
      if (!result) {
        throw new AppError({ code: ErrorCodes.SYSTEM_INTERNAL_ERROR, message: `Insert succeeded but row not found: ${table}/${insertedId}` }, 500)
      }
      return result
    } catch (err) {
      handleDbError(err)
    }
  }

  async update<T = unknown>(table: string, id: string, data: Record<string, unknown>): Promise<T> {
    try {
      await this.knex(table).where('id', id).update(data)
      const result = await this.findById<T>(table, id)
      if (!result) {
        throw new AppError({ code: ErrorCodes.RESOURCE_NOT_FOUND, message: `Row not found after update: ${table}/${id}` }, 404)
      }
      return result
    } catch (err) {
      handleDbError(err)
    }
  }

  async delete(table: string, id: string): Promise<boolean> {
    try {
      const deleted = await this.knex(table).where('id', id).delete()
      return deleted > 0
    } catch (err) {
      handleDbError(err)
    }
  }

  async transaction<T>(fn: (trx: DatabaseAdapter) => Promise<T>): Promise<T> {
    return this.knex.transaction(async (trx) => {
      const trxAdapter = new KnexAdapter(trx as unknown as Knex)
      return fn(trxAdapter)
    })
  }
}

/**
 * Create a KnexAdapter instance.
 *
 * @param knex - Knex instance
 * @returns DatabaseAdapter implementation
 */
export function createKnexAdapter(knex: Knex): DatabaseAdapter {
  return new KnexAdapter(knex)
}
